import { TrendingDown, TrendingUp } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { formatValue } from '@/_shared/lib/charts/format';
import { cn } from '@/_shared/lib/utils';

interface MetricCardProps {
  title: string;
  value: number | null;
  format?: 'number' | 'percent' | 'currency';
  prefix?: string;
  suffix?: string;
  delta?: number;
  loading?: boolean;
  /** Panel toolbar, rendered on the header row so it lines up with the title. */
  actions?: React.ReactNode;
}

export function MetricCard({
  title,
  value,
  format = 'number',
  prefix,
  suffix,
  delta,
  loading,
  actions,
}: MetricCardProps): React.ReactElement {
  // CardHeader is a grid whose `auto` column grows to the row's min-content — for a `truncate` title
  // that is the whole unwrapped string, which pushed the header past the card. `min-w-0` on the row
  // lets the column shrink so the title truncates in place; the full text rides the `title` tooltip.
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <CardTitle className="truncate text-sm font-medium text-muted-foreground" title={title}>
              {title}
            </CardTitle>
            {actions}
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <CardTitle className="truncate text-sm font-medium text-muted-foreground" title={title}>
            {title}
          </CardTitle>
          {actions}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-foreground tabular-nums">
          {value !== null ? formatValue(value, format, prefix, suffix) : '\u2014'}
        </div>
        {delta !== undefined && (
          <div className={cn('mt-1 flex items-center gap-1 text-xs', delta >= 0 ? 'text-success' : 'text-danger')}>
            {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            <span>
              {delta >= 0 ? '+' : ''}
              {delta.toFixed(1)}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
